import { useState, useRef } from 'react'
import * as api from '../utils/api'
import { uploadMedia } from '../utils/media'
import { C, btn, input } from '../constant/theme'

// How many files upload at the same time when many are selected at once.
const UPLOAD_CONCURRENCY = 50

// One editable event: title, time, description, location (+lat/lng), attendees,
// and its media (upload + grid with delete). Saves on blur.
export default function EventCard({ event, media, groupId, onSave, onRemove, onMediaAdded, onMediaRemoved, onMediaUpdated }) {
  const [form, setForm] = useState({
    title: event.title,
    time: event.time,
    description: event.description,
    locName: event.location?.name || '',
    lat: event.location?.lat ?? '',
    lng: event.location?.lng ?? '',
  })
  const [uploads, setUploads] = useState([]) // [{ name, progress }]
  const fileRef = useRef()

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }))

  const saveText = () => onSave(event._id, { title: form.title, time: form.time, description: form.description })
  const saveLocation = () =>
    onSave(event._id, {
      location: {
        name: form.locName,
        lat: form.lat === '' ? null : Number(form.lat),
        lng: form.lng === '' ? null : Number(form.lng),
      },
    })

  const onFiles = async (files) => {
    const jobs = Array.from(files).map((file, i) => ({ key: `${file.name}-${i}-${Date.now()}-${Math.random()}`, file }))
    if (!jobs.length) return

    // Show every selected file in the queue right away, then clear the input so
    // re-picking the same files works.
    setUploads((u) => [...u, ...jobs.map((j) => ({ key: j.key, name: j.file.name, progress: 0 }))])
    if (fileRef.current) fileRef.current.value = ''

    const uploadOne = async ({ key, file }) => {
      try {
        const m = await uploadMedia({
          groupId,
          eventId: event._id,
          file,
          onProgress: (p) => setUploads((u) => u.map((x) => (x.key === key ? { ...x, progress: p } : x))),
        })
        onMediaAdded(m)
      } catch (err) {
        window.dispatchEvent(new CustomEvent('api-toast', { detail: { message: `${file.name}: ${err.message || 'Upload failed'}` } }))
      } finally {
        setUploads((u) => u.filter((x) => x.key !== key))
      }
    }

    // Upload several at a time; each worker pulls the next file as it finishes.
    let cursor = 0
    const worker = async () => {
      while (cursor < jobs.length) await uploadOne(jobs[cursor++])
    }
    await Promise.all(Array.from({ length: Math.min(UPLOAD_CONCURRENCY, jobs.length) }, worker))
  }

  const removeMedia = async (mediaId) => {
    await api.deleteMedia(groupId, mediaId)
    onMediaRemoved(mediaId)
  }

  return (
    <div style={S.card}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3, flex: 1, minWidth: 140 }}>
          <label style={S.label}>Event title</label>
          <input style={{ ...input, fontWeight: 700, width: '100%' }} value={form.title} onChange={set('title')} onBlur={saveText} placeholder="e.g. Beach visit" />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3, width: 110, flexShrink: 0 }}>
          <label style={S.label}>Time</label>
          <input style={{ ...input, width: '100%' }} value={form.time} onChange={set('time')} onBlur={saveText} placeholder="e.g. 10:30 AM" />
        </div>
        <button style={btn('danger')} onClick={() => onRemove(event._id)}>✕</button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 3, marginTop: 10 }}>
        <label style={S.label}>Description</label>
        <textarea style={{ ...input, minHeight: 56, resize: 'vertical' }} value={form.description} onChange={set('description')} onBlur={saveText} placeholder="What happened…" />
      </div>

      <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3, flex: 2, minWidth: 160 }}>
          <label style={S.label}>Location</label>
          <input style={{ ...input, width: '100%' }} value={form.locName} onChange={set('locName')} onBlur={saveLocation} placeholder="e.g. Baga Beach" />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3, flex: 1, minWidth: 90 }}>
          <label style={S.label}>Latitude</label>
          <input style={{ ...input, width: '100%' }} value={form.lat} onChange={set('lat')} onBlur={saveLocation} placeholder="15.5524" inputMode="decimal" />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3, flex: 1, minWidth: 90 }}>
          <label style={S.label}>Longitude</label>
          <input style={{ ...input, width: '100%' }} value={form.lng} onChange={set('lng')} onBlur={saveLocation} placeholder="73.7625" inputMode="decimal" />
        </div>
      </div>

      {/* Media grid */}
      <div style={S.mediaGrid}>
        {media
          .slice()
          .sort((a, b) => a.order - b.order)
          .map((m) => (
            <MediaThumb key={m._id} m={m} groupId={groupId} onRemove={removeMedia} onUpdated={onMediaUpdated} />
          ))}

        {uploads.map((u) => (
          <div key={u.key} style={{ ...S.thumb, ...S.uploading }}>
            <span style={{ fontSize: 11, color: C.textSoft }}>{Math.round(u.progress * 100)}%</span>
            <div style={S.progressTrack}><div style={{ ...S.progressBar, width: `${u.progress * 100}%` }} /></div>
          </div>
        ))}

        <label style={S.addThumb}>
          + Media
          <input ref={fileRef} type="file" accept="image/*,video/*" multiple hidden onChange={(e) => onFiles(e.target.files)} />
        </label>
      </div>
    </div>
  )
}

// One media thumbnail with a "Posted by" field that saves on blur.
function MediaThumb({ m, groupId, onRemove, onUpdated }) {
  const [postedBy, setPostedBy] = useState(m.postedBy || '')

  const save = async () => {
    const next = postedBy.trim()
    if (next === (m.postedBy || '')) return
    const updated = await api.updateMedia(groupId, m._id, { postedBy: next })
    onUpdated && onUpdated(updated)
  }

  return (
    <div style={S.mediaItem}>
      <div style={S.thumb}>
        {m.type === 'video' ? (
          <video src={m.url} style={S.thumbMedia} muted />
        ) : (
          <img src={m.url} alt={m.caption || ''} style={S.thumbMedia} />
        )}
        {m.type === 'video' && <span style={S.playBadge}>▶</span>}
        {m.uncompressed && <span style={S.uncompressedBadge}>Uncompressed</span>}
        <button style={S.thumbDel} onClick={() => onRemove(m._id)}>✕</button>
      </div>
      <label style={S.posterLabel}>Posted by</label>
      <input
        style={S.posterInput}
        value={postedBy}
        onChange={(e) => setPostedBy(e.target.value)}
        onBlur={save}
        placeholder="Name…"
      />
    </div>
  )
}

const S = {
  label: { fontSize: 12, fontWeight: 600, color: '#2e3d52' },
  posterLabel: { fontSize: 10, fontWeight: 600, color: '#2e3d52', marginTop: 2 },
  card: { background: '#fff', border: `1px solid ${C.cardBorder}`, borderRadius: 12, padding: 14 },
  mediaGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(92px, 1fr))', gap: 8, marginTop: 12, alignItems: 'start' },
  mediaItem: { display: 'flex', flexDirection: 'column', gap: 4 },
  posterInput: { width: '100%', boxSizing: 'border-box', padding: '4px 6px', fontSize: 11, border: `1px solid ${C.inputBorder}`, borderRadius: 6, background: C.inputBg, color: C.text, outline: 'none', fontFamily: 'inherit' },
  thumb: { position: 'relative', aspectRatio: '1', borderRadius: 8, overflow: 'hidden', background: '#e4e9f1' },
  thumbMedia: { width: '100%', height: '100%', objectFit: 'cover' },
  playBadge: { position: 'absolute', left: 6, bottom: 6, color: '#fff', fontSize: 12, textShadow: '0 1px 3px rgba(0,0,0,.6)' },
  uncompressedBadge: { position: 'absolute', left: 4, top: 4, fontSize: 9, fontWeight: 700, padding: '2px 5px', borderRadius: 5, background: 'rgba(180,90,16,.92)', color: '#fff', letterSpacing: '.02em' },
  thumbDel: { position: 'absolute', top: 4, right: 4, width: 20, height: 20, borderRadius: '50%', border: 'none', background: 'rgba(20,30,45,.7)', color: '#fff', cursor: 'pointer', fontSize: 11, lineHeight: 1 },
  uploading: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6 },
  progressTrack: { width: '70%', height: 4, background: '#cdd6e2', borderRadius: 4, overflow: 'hidden' },
  progressBar: { height: '100%', background: C.navy2 },
  addThumb: { aspectRatio: '1', border: `1.5px dashed ${C.inputBorder}`, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: 12, color: C.muted, fontWeight: 600 },
}
